import { LT_TAB_INDEX } from '../LegalTransactionSidebar';
import * as clients from './rules/clients';
import * as gwg from './rules/gwg';
import * as invoices from './rules/invoices';
import * as masterData from './rules/masterData';
import { IHealthCheckRuleDefinition } from './types';

/**
 * Regelkatalog des Health Checks (#1283).
 *
 * Der Katalog ist die einzige Quelle der Wahrheit für die Regeln. In der
 * Datenbank steht nur, OB und WO eine Regel greift — eine fehlende
 * Konfigurationszeile bedeutet „Default von hier". Eine neue Regel braucht
 * deshalb weder Migration noch Änderung an der Einstellungs-Oberfläche: sie
 * erscheint automatisch, sobald sie hier eingetragen ist.
 *
 * `defaultActive` ist fuer fast alles `false`. Der Health Check ist fuer die
 * Kanzlei eine Erweiterung: eine Regel, die es vorher nicht gab, soll nach
 * einem Update nichts veraendern, was niemand entschieden hat. Ein
 * Administrator schaltet unter Einstellungen → Health Check → Vorgang-Check
 * gezielt frei, was er haben will.
 *
 * Ausgenommen sind elf Regeln, die eine Warnung ersetzen, die es **vorher
 * schon gab** — die Warnleisten am Zahlungsschuldner, die Erreichbarkeits-
 * hinweise im Reiter Mandanten und die Alerts im Kopf des Vorgangs. Sie sind
 * `true`, weil sie sonst mit diesem Umbau ersatzlos verschwaenden. Welche das
 * sind und warum, steht in `catalogDefaults.test.ts`; ein Zuwiderhandeln macht
 * den Test rot.
 *
 * Zwei Regeln beim Erweitern:
 *
 *  1. Der Schlüssel ist stabil. Er steht in der Datenbank und im
 *     Aktivitätsprotokoll — ihn nachträglich zu ändern verliert die
 *     Konfiguration aller Kanzleien und macht alte Logeinträge unlesbar.
 *  2. Eine Regel = eine Severity. Wenn eine Prüfung je nach Ausprägung
 *     unterschiedlich schwer wiegt, gehören das zwei Regeln, damit die Kanzlei
 *     sie einzeln abschalten kann.
 */
export const HEALTH_CHECK_CATALOG: Record<string, IHealthCheckRuleDefinition> =
  {
    // ---------------------------------------------------------------- Stammdaten
    'master.missing-notarization-date': {
      title: 'Kein Datum der Beurkundung',
      description:
        'Warnt, wenn kein Beurkundungsdatum hinterlegt ist. Das Datum ist der Startpunkt aller Maßnahmen-Fristen des Vorgangs.',
      section: 'Stammdaten',
      defaultSeverity: 'warning',
      defaultActive: false,
      evaluate: masterData.missingNotarizationDate,
    },
    'master.missing-employee': {
      title: 'Kein Sachbearbeiter zugewiesen',
      description:
        'Warnt, wenn dem Vorgang weder ein Sachbearbeiter noch eine Benutzergruppe zugewiesen ist.',
      section: 'Stammdaten',
      defaultSeverity: 'warning',
      defaultActive: false,
      evaluate: masterData.missingEmployee,
    },
    'master.missing-administrative-unit': {
      title: 'Keine Gemeinde/Gemarkung',
      description:
        'Warnt, wenn keine Gemeinde/Gemarkung hinterlegt ist. Sie ist die Vorgabe für alle Phasen des Vorgangs.',
      section: 'Stammdaten',
      defaultSeverity: 'warning',
      defaultActive: false,
      evaluate: masterData.missingAdministrativeUnit,
    },
    'master.missing-title': {
      title: 'Keine Bezeichnung',
      description:
        'Warnt, wenn der Vorgang keine Bezeichnung hat. Bei der Neuanlage wird das bereits erzwungen, beim Bearbeiten nicht.',
      section: 'Stammdaten',
      defaultSeverity: 'warning',
      defaultActive: false,
      evaluate: masterData.missingTitle,
    },
    'master.missing-record-number': {
      title: 'Keine Urkundennummer',
      description:
        'Warnt, wenn ab Status „Laufend" keine Urkundennummer hinterlegt ist.',
      section: 'Stammdaten',
      defaultSeverity: 'warning',
      defaultActive: false,
      evaluate: masterData.missingRecordNumber,
    },
    'master.missing-transaction-number': {
      title: 'Keine Vorgangsnummer',
      description: 'Warnt, wenn keine Vorgangsnummer hinterlegt ist.',
      section: 'Stammdaten',
      defaultSeverity: 'warning',
      defaultActive: false,
      evaluate: masterData.missingTransactionNumber,
    },
    'master.not-visible-for-clients': {
      title: 'Noch keine Bereitstellung im Mandantenportal',
      description:
        'Hinweis, dass Mandanten den Vorgang erst im Status „Laufend" sehen. Zählt als Hinweis nicht in die Warnungsanzahl.',
      section: 'Stammdaten',
      defaultSeverity: 'info',
      defaultActive: true,
      evaluate: masterData.notYetVisibleForClients,
    },
    'master.type-change-needs-migration': {
      title: 'Rechtsgeschäftsart geändert',
      description:
        'Warnt, wenn die Rechtsgeschäftsart verändert wurde und der Vorgang migriert werden muss.',
      section: 'Stammdaten',
      defaultSeverity: 'warning',
      defaultActive: true,
      unavailableReason:
        'Der gespeicherte Stand der Rechtsgeschäftsart lag nicht vor, eine Änderung ist damit nicht erkennbar.',
      evaluate: masterData.typeChangeNeedsMigration,
    },

    // ----------------------------------------------------------------- Mandanten
    'client.none-assigned': {
      title: 'Kein Mandant zugeordnet',
      description:
        'Warnt, wenn dem Vorgang kein Beteiligter zugeordnet ist. Bei Vorlagen-Vorgängen ist das der Normalfall — dort lohnt der Geltungsbereich.',
      section: 'Mandanten',
      defaultSeverity: 'error',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.CLIENTS,
      evaluate: clients.noneAssigned,
    },
    'client.missing-email': {
      title: 'Mandant ohne E-Mail-Adresse',
      description:
        'Warnt bei Beteiligten mit aktiver Erinnerung, für die keine E-Mail-Adresse hinterlegt ist. Sie können sich nicht im Portal anmelden und erhalten keine Benachrichtigungen.',
      section: 'Mandanten',
      defaultSeverity: 'error',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.CLIENTS,
      evaluate: clients.missingEmail,
    },
    'client.no-portal-access': {
      title: 'Mandant ohne Portalzugang',
      description:
        'Warnt bei Beteiligten mit E-Mail-Adresse, die sich dennoch nicht im Mandantenportal anmelden können.',
      section: 'Mandanten',
      defaultSeverity: 'error',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.CLIENTS,
      evaluate: clients.noPortalAccess,
    },
    'client.no-notification': {
      title: 'Mandant mit deaktiviertem E-Mail-Empfang',
      description:
        'Warnt bei Beteiligten, die Dokumente abrufen können, aber keine Benachrichtigungen per E-Mail erhalten.',
      section: 'Mandanten',
      defaultSeverity: 'warning',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.CLIENTS,
      evaluate: clients.noNotification,
    },
    'client.without-role': {
      title: 'Beteiligter ohne Rolle',
      description:
        'Warnt bei Beteiligten ohne hinterlegte Rolle. Ohne Rolle steuert nichts, welche Unterlagen sie im Portal sehen.',
      section: 'Mandanten',
      defaultSeverity: 'error',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.CLIENTS,
      evaluate: clients.withoutRole,
    },
    'client.no-payer-role': {
      title: 'Keine Rolle als Zahlungsschuldner',
      description:
        'Warnt, wenn keine Rolle des Vorgangs als Zahlungsschuldner gekennzeichnet ist. Die automatische Rechnungszuordnung greift dann nicht.',
      section: 'Mandanten',
      defaultSeverity: 'warning',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.CLIENTS,
      evaluate: clients.noPayerRole,
    },
    'client.no-invoice-reminder': {
      title: 'Kein Mandant mit Rechnungserinnerung',
      description:
        'Warnt, wenn bei keinem Beteiligten die Rechnungserinnerung aktiviert ist. Ersetzt den früheren Bestätigungsdialog beim Speichern.',
      section: 'Mandanten',
      defaultSeverity: 'warning',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.CLIENTS,
      evaluate: clients.noInvoiceReminderRecipient,
    },
    'client.no-certificate-reminder': {
      title: 'Kein Mandant mit Urkunden-Erinnerung',
      description:
        'Warnt, wenn Urkunden im Vorgang liegen, aber bei keinem Beteiligten die Urkunden-Erinnerung aktiviert ist.',
      section: 'Urkunden',
      defaultSeverity: 'warning',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.CERTIFICATES,
      evaluate: clients.noCertificateReminderRecipient,
    },

    // ---------------------------------------------------------------- Rechnungen
    'invoice.without-debtor': {
      title: 'Rechnung ohne Zahlungsschuldner',
      description:
        'Warnt bei Rechnungen, denen kein Zahlungsschuldner zugeordnet ist.',
      section: 'Rechnungen',
      defaultSeverity: 'error',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.INVOICES,
      evaluate: invoices.withoutDebtor,
    },
    'invoice.debtor-missing-email': {
      title: 'Zahlungsschuldner ohne E-Mail-Adresse',
      description:
        'Warnt bei Zahlungsschuldnern ohne E-Mail-Adresse. Sie erhalten die Rechnung nicht.',
      section: 'Rechnungen',
      defaultSeverity: 'error',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.INVOICES,
      evaluate: invoices.debtorMissingEmail,
    },
    'invoice.debtor-no-portal-access': {
      title: 'Zahlungsschuldner ohne Portalzugang',
      description:
        'Warnt bei Zahlungsschuldnern, die sich nicht im Mandantenportal anmelden können und die Rechnung daher nicht öffnen.',
      section: 'Rechnungen',
      defaultSeverity: 'error',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.INVOICES,
      evaluate: invoices.debtorNoPortalAccess,
    },
    'invoice.debtor-no-notification': {
      title: 'Zahlungsschuldner mit deaktiviertem E-Mail-Empfang',
      description:
        'Warnt bei Zahlungsschuldnern, die die Rechnung abrufen können, aber keine Benachrichtigung erhalten.',
      section: 'Rechnungen',
      defaultSeverity: 'warning',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.INVOICES,
      evaluate: invoices.debtorNoNotification,
    },
    'invoice.record-number-without-invoice': {
      title: 'Urkundennummer ohne Rechnung',
      description:
        'Warnt, wenn zu einer Urkundennummer keine Rechnung vorliegt oder eine Rechnung keiner Urkundennummer zugeordnet ist.',
      section: 'Rechnungen',
      defaultSeverity: 'warning',
      defaultActive: true,
      tabIndex: LT_TAB_INDEX.INVOICES,
      evaluate: invoices.recordNumberWithoutInvoice,
    },
    'invoice.unrecognized': {
      title: 'Nicht erkannte Rechnungen',
      description:
        'Warnt, wenn Dateien als nicht erkannte Rechnungen im Vorgang liegen und noch keiner Rechnung zugeordnet wurden.',
      section: 'Rechnungen',
      defaultSeverity: 'warning',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.INVOICES,
      evaluate: invoices.unrecognizedInvoices,
    },
    'invoice.without-amount': {
      title: 'Rechnung ohne Betrag',
      description: 'Warnt bei Rechnungen ohne Betrag oder mit Betrag 0,00 €.',
      section: 'Rechnungen',
      defaultSeverity: 'warning',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.INVOICES,
      evaluate: invoices.withoutAmount,
    },
    'invoice.payment-overdue': {
      title: 'Zahlungsziel überschritten',
      description:
        'Warnt bei Rechnungen, deren Zahlungsziel überschritten ist und für die keine Zahlung verbucht wurde.',
      section: 'Rechnungen',
      defaultSeverity: 'warning',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.INVOICES,
      params: [
        {
          key: 'graceDays',
          label: 'Karenztage',
          type: 'number',
          default: 7,
          helperText:
            'Tage nach dem Zahlungsziel, ab denen gewarnt wird. 0 = ab dem Tag danach.',
        },
      ],
      evaluate: invoices.paymentOverdue,
    },

    // ----------------------------------------------------------------------- GwG
    'gwg.mandatory-documents-missing': {
      title: 'GwG-Pflichtunterlagen fehlen',
      description:
        'Warnt, wenn im GwG-Ordner Pflichtunterlagen fehlen. Bisher nur als Statusanzeige über der Ansicht sichtbar.',
      section: 'GwG',
      defaultSeverity: 'warning',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.INTERNAL_DOCUMENTS,
      evaluate: gwg.mandatoryDocumentsMissing,
    },
    'gwg.no-mandatory-document-types': {
      title: 'GwG-Ordner ohne Pflichtunterlage',
      description:
        'Warnt, wenn ein GwG-Ordner existiert, aber keine Dokumentart als Pflicht festgelegt ist — der Ordner überwacht dann nichts.',
      section: 'GwG',
      defaultSeverity: 'warning',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.INTERNAL_DOCUMENTS,
      evaluate: gwg.noMandatoryDocumentTypes,
    },
    'gwg.document-expiring': {
      title: 'GwG-Unterlage abgelaufen',
      description:
        'Warnt bei GwG-Unterlagen, deren Gültigkeit abgelaufen ist oder bald abläuft. Die Vollständigkeitsprüfung berücksichtigt das Ablaufdatum nicht.',
      section: 'GwG',
      defaultSeverity: 'warning',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.INTERNAL_DOCUMENTS,
      params: [
        {
          key: 'leadDays',
          label: 'Vorwarnzeit in Tagen',
          type: 'number',
          default: 30,
          helperText:
            'Wie viele Tage vor Ablauf gewarnt wird. 0 = erst nach Ablauf.',
        },
      ],
      evaluate: gwg.documentExpiring,
    },
    'gwg.folder-suggested': {
      title: 'GwG-Ordner empfohlen',
      description:
        'Hinweis, dass ein GwG-Ordner empfohlen wird (eine juristische Person ist am Vorgang beteiligt). Zählt als Hinweis nicht in die Warnungsanzahl.',
      section: 'GwG',
      defaultSeverity: 'info',
      defaultActive: false,
      tabIndex: LT_TAB_INDEX.INTERNAL_DOCUMENTS,
      evaluate: gwg.folderSuggested,
    },
  };

/** Alle Regelschlüssel in Katalog-Reihenfolge. */
export const HEALTH_CHECK_RULE_KEYS = Object.keys(HEALTH_CHECK_CATALOG);
